import { ReactNode } from "react";
import { SpinnerIcon } from "@/components/icons";
import { cn } from "@/utils/cn";

export interface Stat {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
}

interface StatStripProps {
  stats: Stat[];
  /** A row under the numbers, e.g. the Money tab's month bar. */
  footer?: ReactNode;
  /**
   * One height for every tab of a page (DD-042: switching tabs never moves the filter bar or table). From lg, where
   * the numbers sit in one row, each sub takes two lines at most and keeps room for two, and the footer row is kept
   * when a tab has none: at one text line, or at the month bar's height with `barHeight`. Below lg the cells stack two a row, so the height follows the count.
   */
  steady?: boolean;
  /**
   * Keep the footer row at the Money tab's month bar height, for the agency Finance tabs that share one height. A
   * steady strip without it keeps the one-line reservation, for pages that never show a bar.
   */
  barHeight?: boolean;
  className?: string;
}

/** The footer row's height from lg for a strip with no bar: one text line over where a bar would sit. */
const STEADY_FOOTER = "lg:h-[2.125rem]";

/**
 * The footer row's height from lg when `barHeight` is set: the Money tab's month bar, its text line and its basis line
 * over the bar itself, with its legend's own line under the bar until xl puts the legend on the text line.
 */
const BAR_FOOTER = "lg:h-[4.875rem] xl:h-[3.5rem]";

/** A row of headline numbers inside one panel body: flat cells split by hairlines, no nested cards (DD-002). */
export function StatStrip({ stats, footer, steady = false, barHeight = false, className }: StatStripProps) {
  const grid = (
    <div className={cn("grid grid-cols-2 gap-y-3 lg:grid-flow-col lg:auto-cols-fr lg:divide-x divide-gray-100", !footer && !steady && className)}>
      {stats.map((s) => (
        <div key={s.label} className="min-w-0 pr-4 py-1 lg:px-4 lg:py-2 lg:first:pl-0">
          <div className="text-xs text-gray-500 truncate">{s.label}</div>
          <div className="mt-1 text-xl font-semibold tabular-nums text-gray-900 truncate">{s.value}</div>
          {s.sub && (
            <div className={cn("mt-0.5 text-xs text-gray-400", steady && "line-clamp-2 lg:min-h-8")} title={steady && typeof s.sub === "string" ? s.sub : undefined}>
              {s.sub}
            </div>
          )}
          {!s.sub && steady && <div className="hidden lg:block mt-0.5 min-h-8" aria-hidden />}
        </div>
      ))}
    </div>
  );
  if (!footer && !steady) return grid;
  return (
    <div className={cn("space-y-3", className)}>
      {grid}
      {footer ?? <div className={cn("hidden lg:block", barHeight ? BAR_FOOTER : STEADY_FOOTER)} aria-hidden />}
    </div>
  );
}

/**
 * A steady strip without its numbers, in the strip's own height so nothing below moves (DD-001, DD-042): one spinner
 * while they load, or `children` (the strip's own Retry) once they failed. `failed` with no children leaves the slot
 * empty, for a tab whose body already says what failed. `barHeight` matches a strip that sets it.
 */
export function StatStripPending({ failed = false, barHeight = false, children }: { failed?: boolean; barHeight?: boolean; children?: ReactNode }) {
  return (
    <div className={cn("h-24 flex items-center justify-center", barHeight ? "lg:h-[11.75rem] xl:h-[10.375rem]" : "lg:h-36")}>
      {children ?? (failed ? null : <SpinnerIcon className="w-5 h-5 text-gray-400" />)}
    </div>
  );
}
